import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Grid,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  Autocomplete,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { AdminDataContext } from "../../../useContext/AdminContext";
import { useNavigate } from "react-router-dom";
import API from "../../../api";

const CreateProduct = () => {
  const { snackbar, setIsLoading } = useContext(AdminDataContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    type: "physical",
    weight: "",
    sku: "",
    price: "",
    cost_price: "",
    retail_price: "",
    sale_price: "",
    description: "",
    inventory_level: "",
    inventory_warning_level: "",
    is_visible: true,
    is_featured: false,
    is_custom_product: false,
    is_bundle: false,
    categories: [],
    bundleItems: [],
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [bundleSearchQuery, setBundleSearchQuery] = useState("");
  const [bundleSearchResults, setBundleSearchResults] = useState([]);
  const [bundleSearchLoading, setBundleSearchLoading] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [categorySearchTimeout, setCategorySearchTimeout] = useState(null);
  const [errorMessage, setErrorMessage] = useState({
    name: "",
    sku: "",
    error: false,
    generalError: "Character limit exceeded in bundle name or bundle sku",
  });
  // Form validation
  const [errors, setErrors] = useState({});

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();

    // Cleanup timeout on unmount
    return () => {
      if (categorySearchTimeout) {
        clearTimeout(categorySearchTimeout);
      }
    };
  }, []);

  // Calculate bundle prices, weight, and inventory level when bundle items change
  useEffect(() => {
    if (
      Array.isArray(formData.bundleItems) &&
      formData.bundleItems.length > 0
    ) {
      // Calculate all price types by summing up bundle items
      const totalPrice = formData.bundleItems.reduce(
        (sum, item) => sum + (item?.price || item?.calculated_price || 0),
        0
      );

      const totalCostPrice = formData.bundleItems.reduce(
        (sum, item) => sum + (item?.cost_price || 0),
        0
      );

      const totalRetailPrice = formData.bundleItems.reduce(
        (sum, item) => sum + (item?.retail_price || 0),
        0
      );

      const totalSalePrice = formData.bundleItems.reduce(
        (sum, item) => sum + (item?.sale_price || 0),
        0
      );

      // Calculate total weight by summing up bundle items
      const totalWeight = formData.bundleItems.reduce(
        (sum, item) => sum + (item?.weight || 0),
        0
      );
      console.log("formData.bundleItems", formData.bundleItems);

      // Calculate smallest inventory level from bundle items

      const trackableItems = formData.bundleItems.filter(
        (item) => item?.inventory_tracking === "product"
      );

      const smallestInventoryLevel = trackableItems.length
        ? Math.min(...trackableItems.map((item) => item?.inventory_level || 0))
        : 0;
      console.log("smallestInventoryLevel", smallestInventoryLevel);

      setCalculatedPrice(totalPrice);
      setFormData((prev) => ({
        ...prev,
        price: totalPrice.toString(), // Always update to calculated price when items change
        cost_price: totalCostPrice.toString(),
        retail_price: totalRetailPrice.toString(),
        sale_price: totalSalePrice.toString(),
        weight: totalWeight.toString(),
        inventory_level: smallestInventoryLevel.toString(),
        calculated_price: totalPrice,
      }));
    } else {
      // Reset prices, weight, and inventory level when no bundle items
      setCalculatedPrice(0);
      setFormData((prev) => ({
        ...prev,
        price: "",
        cost_price: "",
        retail_price: "",
        sale_price: "",
        weight: "",
        inventory_level: "",
        calculated_price: 0,
      }));
    }
  }, [formData.bundleItems]);

  // Fetch categories
  const fetchCategories = async (searchQuery = "") => {
    try {
      const url = searchQuery
        ? `/api/v1/category/categories?search=${encodeURIComponent(
            searchQuery
          )}`
        : "/api/v1/category/categories";

      const response = await API.get(url);
      if (response.data.success) {
        // Handle the nested structure: response.data.data.categories
        const categoriesData =
          response.data.data?.categories || response.data.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      snackbar("Error fetching categories", "error");
      setCategories([]); // Ensure categories is always an array
    }
  };

  // Search for products to add to bundle
  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setBundleSearchResults([]);
      return;
    }

    try {
      setBundleSearchLoading(true);
      const response = await API.get(
        `/api/v1/products/products?search=${query}&limit=10`
      );
      if (response.data.success) {
        const products = response.data.data.products || [];
        // Filter out products that are already in the bundle
        const filteredProducts = products.filter(
          (product) =>
            !formData.bundleItems.some((item) => item.id === product.id)
        );
        setBundleSearchResults(filteredProducts);
      }
    } catch (error) {
      console.error("Error searching products:", error);
      snackbar("Error searching products", "error");
    } finally {
      setBundleSearchLoading(false);
    }
  };

  // Handle bundle search
  const handleBundleSearch = (event, value) => {
    setBundleSearchQuery(value);
    searchProducts(value);
  };

  // Add product to bundle
  const addToBundle = (product) => {
    setFormData((prev) => ({
      ...prev,
      bundleItems: [...prev.bundleItems, product],
    }));
    setBundleSearchQuery("");
    setBundleSearchResults([]);
  };

  // Remove product from bundle
  const removeFromBundle = (productId) => {
    setFormData((prev) => ({
      ...prev,
      bundleItems: prev.bundleItems.filter((item) => item.id !== productId),
    }));
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Real-time validation for name field
    if (field === "name") {
      if (value.length > 250) {
        // setErrorMessage("Product name must be less than 250 characters");
        setErrorMessage((prev) => ({
          ...prev,
          error: true,
          name: "Product name must be less than 250 characters",
        }));
      } else {
        // setErrorMessage("");
        setErrorMessage((prev) => ({ ...prev, name: "", error: false }));
      }
    }
    if (field === "sku") {
      if (value.length > 250) {
        // setErrorMessage("Product name must be less than 250 characters");
        setErrorMessage((prev) => ({
          ...prev,
          error: true,
          sku: "Product sku must be less than 250 characters",
        }));
      } else {
        // setErrorMessage("");
        setErrorMessage((prev) => ({ ...prev, sku: "", error: false }));
      }
    }
  };

  // Handle switch changes
  const handleSwitchChange = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Product name is required";
    }
    // length validation for name and sku
    if (formData.name.length > 250) {
      setErrorMessage((prev) => ({
        ...prev,
        error: true,
        name: "Product name must be less than 250 characters",
      }));
      newErrors.name = "Product name must be less than 250 characters";
    }

    if (formData.sku.length > 250) {
      setErrorMessage((prev) => ({
        ...prev,
        error: true,
        sku: "Product sku must be less than 250 characters",
      }));
      newErrors.sku = "Product sku must be less than 250 characters";
    }

    if (!formData.sku.trim()) {
      newErrors.sku = "SKU is required";
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = "Valid price is required";
    }

    if (
      !Array.isArray(formData.categories) ||
      formData.categories.length === 0
    ) {
      newErrors.categories = "At least one category is required";
    }

    if (
      !Array.isArray(formData.bundleItems) ||
      formData.bundleItems.length === 0
    ) {
      newErrors.bundleItems =
        "At least one bundle item is required for bundle products";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      console.log("errorMessage", errorMessage);
      snackbar(
        errorMessage.error
          ? errorMessage.generalError
          : "Oops! Looks like you missed a required field.",
        "error"
      );
      return;
    }

    try {
      setLoading(true);
      setIsLoading(true);

      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        cost_price: parseFloat(formData.cost_price) || 0,
        retail_price: parseFloat(formData.retail_price) || 0,
        sale_price: parseFloat(formData.sale_price) || 0,
        calculated_price: formData.is_bundle
          ? calculatedPrice
          : parseFloat(formData.price),
        inventory_level: parseInt(formData.inventory_level) || 0,
        inventory_warning_level:
          parseInt(formData.inventory_warning_level) || 0,
        weight: parseFloat(formData.weight) || 0,
        // Send only category IDs, not full objects
        categories: Array.isArray(formData.categories)
          ? formData.categories
              .map((cat) => cat?.category_id || cat?.id || cat?._id)
              .filter(Boolean)
          : [],
        // Send only bundle item IDs, not full objects
        bundle_items: Array.isArray(formData.bundleItems)
          ? formData.bundleItems.map((item) => item?.id).filter(Boolean)
          : [],
        bCProductCustomField:
          formData.is_custom_product || formData.is_bundle
            ? {
                is_custom_product: formData.is_custom_product,
                is_bundle: formData.is_bundle,
                bundle_items: Array.isArray(formData.bundleItems)
                  ? formData.bundleItems.map((item) => item?.id).filter(Boolean)
                  : [],
              }
            : undefined,
      };

      // Remove unwanted fields from the payload
      delete productData.bundleItems;
      delete productData.is_bundle;
      delete productData.is_custom_product;

      // Debug: Log the payload to see what we're sending
      console.log("Product Data Payload:", productData);

      const response = await API.post(
        "/api/v1/products/create-product",
        productData
      );

      if (response.data.success) {
        snackbar("Product created successfully!", "success");
        // Navigate to products page with refresh parameter to fetch latest data
        navigate("/products?refresh=true");
      } else {
        snackbar(response.data.message || "Failed to create product", "error");
      }
    } catch (error) {
      console.error("Error creating product:", error.response.data);
      snackbar(
        error.response.data.message || "Error creating product",
        "error"
      );
    } finally {
      setLoading(false);
      setIsLoading(false);
      // navigate("/products");
      // handleBack();
    }
  };

  // Handle back navigation
  const handleBack = () => {
    // Navigate back to products page
    navigate("/products");
  };

  const handleReset = () => {
    setFormData({
      name: "",
      type: "physical",
      weight: "",
      sku: "",
      price: "",
      cost_price: "",
      retail_price: "",
      sale_price: "",
      description: "",
      inventory_level: "",
      inventory_warning_level: "",
      is_visible: true,
      is_featured: false,
      is_custom_product: false,
      is_bundle: false,
      categories: [],
      bundleItems: [],
    });
    setCalculatedPrice(0);
    setErrors({});
    setErrorMessage({
      name: "",
      sku: "",
      error: false,
      generalError: "Character limit exceeded in bundle name or bundle sku",
    });
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton
            onClick={handleBack}
            sx={{
              mr: 2,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          >
            Create New Bundle
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Add a new Bundle to your catalog with comprehensive details
        </Typography>
      </Box>

      {/* Error Message Display */}
      {errorMessage.name && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setErrorMessage((prev) => ({ ...prev, name: "" }))}
        >
          {errorMessage.name}
        </Alert>
      )}
      {errorMessage.sku && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setErrorMessage((prev) => ({ ...prev, sku: "" }))}
        >
          {errorMessage.sku}
        </Alert>
      )}

      {/* Stepper - Removed since not using steps */}

      {/* Form Content */}
      <Card>
        <CardContent>
          {/* Basic Information */}
          <Box>
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Basic Information
              </Typography>
              <Grid container spacing={2} padding={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Bundle Name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Bundle SKU"
                    value={formData.sku}
                    onChange={(e) => handleChange("sku", e.target.value)}
                    error={!!errors.sku}
                    helperText={errors.sku}
                    required
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Bundle Type</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) => handleChange("type", e.target.value)}
                      label="Bundle  Type"
                    >
                      <MenuItem value="physical">Physical</MenuItem>
                      <MenuItem value="digital">Digital</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {/* <TextField
                    fullWidth
                    label="Weight (kg)"
                    type="number"
                    value={formData.weight}
                    onChange={(e) => handleChange("weight", e.target.value)}
                    inputProps={{ step: 0.1, min: 0 }}
                    sx={{ mb: 2 }}
                  /> */}
                  <Autocomplete
                    multiple
                    options={Array.isArray(categories) ? categories : []}
                    getOptionLabel={(option) => option?.name || ""}
                    value={
                      Array.isArray(formData.categories)
                        ? formData.categories
                        : []
                    }
                    onChange={(event, newValue) =>
                      handleChange("categories", newValue)
                    }
                    onInputChange={(event, newInputValue) => {
                      // Clear previous timeout
                      if (categorySearchTimeout) {
                        clearTimeout(categorySearchTimeout);
                      }

                      // Set new timeout for debounced search
                      const timeout = setTimeout(() => {
                        if (newInputValue && newInputValue.length >= 2) {
                          fetchCategories(newInputValue);
                        } else if (newInputValue === "") {
                          fetchCategories(); // Load all categories when input is cleared
                        }
                      }, 300); // 300ms delay

                      setCategorySearchTimeout(timeout);
                    }}
                    filterOptions={(options, { inputValue }) => {
                      // Filter out already selected categories from options
                      const selectedCategoryIds = Array.isArray(
                        formData.categories
                      )
                        ? formData.categories.map((cat) => cat?._id || cat?.id)
                        : [];

                      const filteredOptions = options.filter(
                        (option) =>
                          !selectedCategoryIds.includes(
                            option?._id || option?.id
                          )
                      );

                      // Also filter by input value for better UX
                      if (inputValue) {
                        return filteredOptions.filter((option) =>
                          option?.name
                            ?.toLowerCase()
                            .includes(inputValue.toLowerCase())
                        );
                      }

                      return filteredOptions;
                    }}
                    noOptionsText={
                      Array.isArray(formData.categories) &&
                      formData.categories.length > 0
                        ? "No more categories available to select"
                        : "No categories found"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Categories"
                        error={!!errors.categories}
                        helperText={
                          errors.categories ||
                          `${
                            Array.isArray(formData.categories)
                              ? formData.categories.length
                              : 0
                          } category(ies) selected`
                        }
                        required
                        placeholder="Type to search categories..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      (Array.isArray(value) ? value : []).map(
                        (option, index) => (
                          <Chip
                            key={index}
                            variant="outlined"
                            label={option?.name || ""}
                            color="primary"
                            {...getTagProps({ index })}
                          />
                        )
                      )
                    }
                    sx={{ mb: 2 }}
                  />
                </Grid>

                {/* Step 4: Bundle Configuration */}
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary">
                      Select Products
                    </Typography>

                    {/* Bundle Search */}
                    <Box sx={{ mb: 3 }}>
                      <Autocomplete
                        freeSolo
                        options={
                          Array.isArray(bundleSearchResults)
                            ? bundleSearchResults
                            : []
                        }
                        getOptionLabel={(option) =>
                          option?.name || option || ""
                        }
                        value={bundleSearchQuery}
                        onInputChange={handleBundleSearch}
                        loading={bundleSearchLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Search products to add to bundle"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {bundleSearchLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            onClick={() => addToBundle(option)}
                            sx={{ cursor: "pointer" }}
                          >
                            <Box>
                              <Typography variant="body1">
                                {option?.name || ""}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                SKU: {option?.sku || ""} | Price: $
                                {option?.calculated_price || 0} | Weight:{" "}
                                {option?.weight || 0} Ounces
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                    </Box>

                    {/* Bundle Items List */}
                    {Array.isArray(formData.bundleItems) &&
                      formData.bundleItems.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            Bundle Items ({formData.bundleItems.length})
                          </Typography>
                          <List>
                            {formData.bundleItems.map((item, index) => (
                              <ListItem key={item?.id || index} divider>
                                <ListItemText
                                  primary={item?.name || ""}
                                  secondary={`SKU: ${
                                    item?.sku || ""
                                  } | Price: $${
                                    item?.calculated_price || 0
                                  } | Weight: ${
                                    item?.weight || 0
                                  } Ounces | Inventory: ${
                                    item?.inventory_level || 0
                                  }`}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    onClick={() => removeFromBundle(item?.id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>

                          {/* Bundle Price Calculation */}
                          <Paper
                            sx={{
                              p: 2,
                              mt: 2,
                              bgcolor: theme.palette.cream.main,
                            }}
                          >
                            <Typography
                              variant="h6"
                              color="primary"
                              gutterBottom
                            >
                              Auto-Calculated Bundle Values
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2">
                                <strong>Bundle Price:</strong> $
                                {calculatedPrice.toFixed(2)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Cost Price:</strong> $
                                {formData.bundleItems
                                  .reduce(
                                    (sum, item) =>
                                      sum + (item?.cost_price || 0),
                                    0
                                  )
                                  .toFixed(2)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Retail Price:</strong> $
                                {formData.bundleItems
                                  .reduce(
                                    (sum, item) =>
                                      sum + (item?.retail_price || 0),
                                    0
                                  )
                                  .toFixed(2)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Sale Price:</strong> $
                                {formData.bundleItems
                                  .reduce(
                                    (sum, item) =>
                                      sum + (item?.sale_price || 0),
                                    0
                                  )
                                  .toFixed(2)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Total Weight:</strong>{" "}
                                {formData.bundleItems
                                  .reduce(
                                    (sum, item) => sum + (item?.weight || 0),
                                    0
                                  )
                                  .toFixed(2)}{" "}
                                Ounces
                              </Typography>
                              <Typography variant="body2">
                                <strong>Inventory Level:</strong>{" "}
                                {/* {Math.min(
                                  ...formData.bundleItems.map(
                                    (item) => item?.inventory_level || 0
                                  )
                                )}{" "} */}
                                {formData.inventory_level || 0}
                                (smallest from bundle items)
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 1, display: "block" }}
                            >
                              These values are automatically calculated from
                              bundle items and can be edited in the form fields
                              above
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                    {errors.bundleItems && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {errors.bundleItems}
                      </Alert>
                    )}
                  </Box>
                </Grid>

                {/* Price Fields */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Bundle Price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleChange("price", e.target.value)}
                    error={!!errors.price}
                    helperText={
                      errors.price ||
                      (calculatedPrice > 0
                        ? `Auto-calculated: $${calculatedPrice.toFixed(
                            2
                          )} (you can edit)`
                        : "Enter bundle price")
                    }
                    required
                    inputProps={{ step: 0.01, min: 0 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Cost Price"
                    type="number"
                    value={formData.cost_price}
                    onChange={(e) => handleChange("cost_price", e.target.value)}
                    helperText={
                      Array.isArray(formData.bundleItems) &&
                      formData.bundleItems.length > 0
                        ? `Auto-calculated: $${formData.bundleItems
                            .reduce(
                              (sum, item) => sum + (item?.cost_price || 0),
                              0
                            )
                            .toFixed(2)} (you can edit)`
                        : "Enter cost price"
                    }
                    inputProps={{ step: 0.01, min: 0 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Retail Price"
                    type="number"
                    value={formData.retail_price}
                    onChange={(e) =>
                      handleChange("retail_price", e.target.value)
                    }
                    helperText={
                      Array.isArray(formData.bundleItems) &&
                      formData.bundleItems.length > 0
                        ? `Auto-calculated: $${formData.bundleItems
                            .reduce(
                              (sum, item) => sum + (item?.retail_price || 0),
                              0
                            )
                            .toFixed(2)} (you can edit)`
                        : "Enter retail price"
                    }
                    inputProps={{ step: 0.01, min: 0 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Sale Price"
                    type="number"
                    value={formData.sale_price}
                    onChange={(e) => handleChange("sale_price", e.target.value)}
                    helperText={
                      Array.isArray(formData.bundleItems) &&
                      formData.bundleItems.length > 0
                        ? `Auto-calculated: $${formData.bundleItems
                            .reduce(
                              (sum, item) => sum + (item?.sale_price || 0),
                              0
                            )
                            .toFixed(2)} (you can edit)`
                        : "Enter sale price"
                    }
                    inputProps={{ step: 0.01, min: 0 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Total Weight (Ounces)"
                    type="number"
                    value={formData.weight}
                    onChange={(e) => handleChange("weight", e.target.value)}
                    helperText={
                      Array.isArray(formData.bundleItems) &&
                      formData.bundleItems.length > 0
                        ? `Auto-calculated: ${formData.bundleItems
                            .reduce((sum, item) => sum + (item?.weight || 0), 0)
                            .toFixed(2)} Ounces (you can edit)`
                        : "Enter total weight"
                    }
                    inputProps={{ step: 0.01, min: 0 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                {/* <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Inventory Level"
                    type="number"
                    value={formData.inventory_level}
                    onChange={(e) => handleChange("inventory_level", e.target.value)}
                    helperText={
                      Array.isArray(formData.bundleItems) && formData.bundleItems.length > 0 
                        ? `Auto-calculated: ${Math.min(...formData.bundleItems.map(item => item?.inventory_level || 0))} (smallest from bundle items, you can edit)` 
                        : "Enter inventory level"
                    }
                    inputProps={{ step: 1, min: 0 }}
                    sx={{ mb: 2 }}
                  />
                </Grid> */}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Pricing & Inventory */}

          {/* Categories & Classification */}

          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_visible}
                        onChange={() => handleSwitchChange("is_visible")}
                        color="primary"
                      />
                    }
                    label="Visible to customers"
                  />
                  {/* <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_featured}
                        onChange={() => handleSwitchChange("is_featured")}
                        color="primary"
                      />
                    }
                    label="Featured product"
                  /> */}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Review & Create */}

          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Review & Create
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: theme.palette.cream.light }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Basic Information
                  </Typography>
                  <Typography variant="body2">Name: {formData.name}</Typography>
                  <Typography variant="body2">SKU: {formData.sku}</Typography>
                  <Typography variant="body2">Type: {formData.type}</Typography>
                  <Typography variant="body2">
                    Weight: {formData.weight || 0} Ounces
                    {Array.isArray(formData.bundleItems) &&
                      formData.bundleItems.length > 0 && (
                        <span style={{ color: "#666", fontSize: "0.8em" }}>
                          {" "}
                          (calculated)
                        </span>
                      )}
                  </Typography>
                  <Typography variant="body2">
                    Inventory Level: {formData.inventory_level || 0}
                    {Array.isArray(formData.bundleItems) &&
                      formData.bundleItems.length > 0 && (
                        <span style={{ color: "#666", fontSize: "0.8em" }}>
                          {" "}
                          (smallest from bundle items)
                        </span>
                      )}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: theme.palette.cream.light }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Pricing{" "}
                    {Array.isArray(formData.bundleItems) &&
                    formData.bundleItems.length > 0
                      ? "(Bundle Total)"
                      : ""}
                  </Typography>
                  <Typography variant="body2">
                    Bundle Price: ${formData.price || 0}
                  </Typography>
                  <Typography variant="body2">
                    Cost Price: ${formData.cost_price || 0}
                  </Typography>
                  <Typography variant="body2">
                    Retail Price: ${formData.retail_price || 0}
                  </Typography>
                  <Typography variant="body2">
                    Sale Price: ${formData.sale_price || 0}
                  </Typography>
                  <Typography variant="body2">
                    Total Weight: {formData.weight || 0} Ounces
                  </Typography>
                  {Array.isArray(formData.bundleItems) &&
                    formData.bundleItems.length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        *Auto-calculated from bundle items (editable)
                      </Typography>
                    )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: theme.palette.cream.light }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Categories
                  </Typography>
                  {Array.isArray(formData.categories) &&
                    formData.categories.map((category, index) => (
                      <Chip
                        key={index}
                        label={category?.name || ""}
                        size="small"
                        color="primary"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: theme.palette.cream.light }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Settings
                  </Typography>
                  <Typography variant="body2">
                    Visible: {formData.is_visible ? "Yes" : "No"}
                  </Typography>
                  {/* <Typography variant="body2">
                    Featured: {formData.is_featured ? "Yes" : "No"}
                  </Typography> */}
                </Paper>
              </Grid>

              {formData.is_bundle &&
                Array.isArray(formData.bundleItems) &&
                formData.bundleItems.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: theme.palette.cream.light }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        color="primary"
                      >
                        Bundle Items ({formData.bundleItems.length})
                      </Typography>
                      {formData.bundleItems.map((item, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            • {item?.name || ""} (SKU: {item?.sku || ""})
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 2 }}
                          >
                            Price: ${item?.price || item?.calculated_price || 0}{" "}
                            | Cost: ${item?.cost_price || 0} | Retail: $
                            {item?.retail_price || 0} | Sale: $
                            {item?.sale_price || 0} | Weight:{" "}
                            {item?.weight || 0} Ounces | Inventory:{" "}
                            {item?.inventory_level || 0}
                          </Typography>
                        </Box>
                      ))}
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" color="primary">
                        Bundle Totals:
                      </Typography>
                      <Typography variant="body2">
                        Total Price: ${calculatedPrice.toFixed(2)}
                      </Typography>
                      <Typography variant="body2">
                        Total Cost: $
                        {formData.bundleItems
                          .reduce(
                            (sum, item) => sum + (item?.cost_price || 0),
                            0
                          )
                          .toFixed(2)}
                      </Typography>
                      <Typography variant="body2">
                        Total Retail: $
                        {formData.bundleItems
                          .reduce(
                            (sum, item) => sum + (item?.retail_price || 0),
                            0
                          )
                          .toFixed(2)}
                      </Typography>
                      <Typography variant="body2">
                        Total Sale: $
                        {formData.bundleItems
                          .reduce(
                            (sum, item) => sum + (item?.sale_price || 0),
                            0
                          )
                          .toFixed(2)}
                      </Typography>
                      <Typography variant="body2">
                        Total Weight:{" "}
                        {formData.bundleItems
                          .reduce((sum, item) => sum + (item?.weight || 0), 0)
                          .toFixed(2)}{" "}
                        Ounces
                      </Typography>
                      <Typography variant="body2">
                        Bundle Inventory Level:{" "}
                        {Math.min(
                          ...formData.bundleItems.map(
                            (item) => item?.inventory_level || 0
                          )
                        )}{" "}
                        (smallest from bundle items)
                      </Typography>
                    </Paper>
                  </Grid>
                )}
            </Grid>
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              color="secondary"
            >
              Back to Products
            </Button>

            <Box>
              <Button onClick={handleReset} sx={{ mr: 2 }} color="secondary">
                Reset
              </Button>

              <Button
                onClick={handleSubmit}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
                color="primary"
              >
                {loading ? "Creating..." : "Create Bundle"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateProduct;
